import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View, ActivityIndicator, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import Switch from '@/components/Inputs/Switch';
import TaskAvatar from '@/components/Tasks/TaskAvatar';
import TaskAttachment from '@/components/Tasks/TaskAttachment';
import TaskFavoriteHeart from '@/components/Tasks/TaskFavoriteHeart';
import {
  useChatStore,
  useInferenceTasksStore,
  useModalStore,
  useToastStore,
} from '@/store';
import {
  taskScheduleIcon,
  taskScheduleHeadline,
  formatRelative,
} from '@/utils/tasks_catalog';

// Saved task card. A neutral schedule band across the top
// reads "DAILY · 8:00 AM"; title + meta sit on a clean body below. Used by
// the /tasks page (and any category route) to render saved tasks. Behaviors
// (run-now, toggle, view results, favorite, edit) match the legacy TaskCard.

function TaskCardCalendar({ task, onPress, showActions = false, layout = 'grid' }) {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { isXS, isSM } = useResponsiveness();
  const router = useRouter();

  const { openModal } = useModalStore(s => s.actions);
  const { addToast } = useToastStore(s => s.actions);
  const toggleTask = useInferenceTasksStore(s => s.toggleTask);
  const runTaskNow = useInferenceTasksStore(s => s.runTaskNow);
  const fetchChats = useChatStore(s => s.fetchChats);

  const [isToggling, setIsToggling] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const enabled = task?.flags?.isEnabled ?? true;
  const hasConversation = !!(task?.conversationId || task?.conversation?.id);

  const styles = useMemo(
    () => createStyles(colors, palette, isXS, isSM, layout),
    [colors, palette, isXS, isSM, layout]
  );

  const handlePress = useCallback(() => {
    if (onPress) onPress(task);
    else openModal('CreateTaskModal', { task });
  }, [onPress, openModal, task]);

  const handleToggle = useCallback(async (next) => {
    if (isToggling) return;
    try {
      setIsToggling(true);
      await toggleTask(task.id, next);
      addToast(next ? t('tasks.enabled') : t('tasks.paused'), 'success');
    } catch (err) {
      addToast(err?.message || t('tasks.updateFailed'), 'error');
    } finally {
      setIsToggling(false);
    }
  }, [task?.id, isToggling, toggleTask, addToast, t]);

  const handleRun = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (isRunning) return;
    try {
      setIsRunning(true);
      const result = await runTaskNow(task.id);
      const conversationId = result?.conversationId;
      if (conversationId) {
        router.push(`/c/${conversationId}`);
      } else {
        addToast(t('tasks.started'), 'success');
        setTimeout(() => fetchChats(1), 2000);
      }
    } catch (err) {
      addToast(err?.message || t('tasks.runFailed'), 'error');
    } finally {
      setIsRunning(false);
    }
  }, [task?.id, isRunning, runTaskNow, addToast, fetchChats, t, router]);

  const handleViewResults = useCallback((e) => {
    e?.stopPropagation?.();
    const id = task?.conversationId || task?.conversation?.id;
    if (id) router.push(`/c/${id}`);
  }, [task, router]);

  return (
    <Pressable
      onPress={handlePress}
      accessible={false}
      {...(Platform.OS === 'web' ? { tabIndex: -1 } : {})}
      testID={`saved-task-${layout}`}
      style={({ hovered }) => [styles.card, hovered && styles.cardHovered]}
    >
      <View style={styles.headerBand}>
        <View style={styles.headerLeft}>
          <Icon name={taskScheduleIcon(task)} size={14} color={palette.icon} />
          <Text size="xs" weight="medium" style={{ color: colors.text_color_secondary, flexShrink: 1 }} numberOfLines={1}>
            {taskScheduleHeadline(task)}
          </Text>
        </View>
        <View style={styles.headerRight}>
          <Icon name={enabled ? 'circle-check' : 'circle-pause'} size={11} color={palette.icon} />
          <Text size="xs" weight="bold" color={enabled ? 'primary' : 'secondary'}>
            {enabled ? t('tasks.enabledStatus') : t('tasks.pausedStatus')}
          </Text>
          <TaskFavoriteHeart task={task} />
        </View>
      </View>

      <View style={styles.body}>
        <View style={styles.content}>
        <Pressable style={styles.titleRow} accessibilityRole="button" accessibilityLabel={task?.name || t('tasks.untitled')}
          onPress={(event) => { event?.stopPropagation?.(); handlePress(); }}>
          <TaskAvatar task={task} size={layout === 'list' ? 32 : 40} />
          <Text size="m" weight="bold" numberOfLines={2} style={{ flex: 1, minWidth: 0 }}>
            {task?.name || t('tasks.untitled')}
          </Text>
        </Pressable>

        <TaskAttachment task={task} />

        <View style={styles.metaRow}>
          {!!task?.nextRunAt && (
            <View style={styles.metaItem}>
              <Icon name="circle-arrow-right" size={11} color={colors.text_color_tertiary} />
              <Text size="xs" color="tertiary">Next {formatRelative(task.nextRunAt)}</Text>
            </View>
          )}
          {!!task?.lastRunAt && (
            <View style={styles.metaItem}>
              <Icon name="circle-check" size={11} color={colors.text_color_tertiary} />
              <Text size="xs" color="tertiary">Last {formatRelative(task.lastRunAt)}</Text>
            </View>
          )}
          {task?.emailDelivery?.isEnabled && (
            <View style={styles.metaItem}>
              <Icon name="envelope" size={11} color={colors.text_color_tertiary} />
              <Text size="xs" color="tertiary">{t('tasks.email')}</Text>
            </View>
          )}

        </View>

        </View>

        {showActions && (
          <View style={styles.actions}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Pressable
                onPress={handleRun}
                accessibilityRole="button"
                accessibilityLabel={t('tasks.runNow')}
                disabled={isRunning}
                style={({ hovered }) => [
                  styles.runBtn,
                  hovered && { backgroundColor: palette.hover, borderColor: palette.activeBorder },
                  isRunning && { opacity: 0.6 },
                ]}
              >
                {isRunning
                  ? <ActivityIndicator size={10} color={colors.text_color_secondary} />
                  : <Icon name="play" size={10} color={colors.text_color_secondary} solid />}
                <Text size="xs" weight="bold" color="secondary">
                  {isRunning ? t('tasks.running') : t('tasks.runNow')}
                </Text>
              </Pressable>
              {hasConversation && (
                <Pressable accessibilityRole="button" accessibilityLabel={t('tasks.results')} onPress={handleViewResults} style={({ hovered }) => [styles.linkBtn, hovered && { backgroundColor: palette.hover }]}>
                  <Icon name="arrow-up-right-from-square" size={10} color={colors.text_color_secondary} />
                  <Text size="xs" color="secondary">{t('tasks.results')}</Text>
                </Pressable>
              )}
            </View>
            <View onClick={(e) => e.stopPropagation()}>
              <Switch
                value={enabled}
                onValueChange={handleToggle}
                disabled={isToggling}
                accessibilityLabel={`${t('tasks.enabledStatus')}: ${task?.name || t('tasks.untitled')}`}
              />
            </View>
          </View>
        )}
      </View>
    </Pressable>
  );
}

export function TaskCardCalendarPlaceholder({ layout = 'grid' }) {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { isXS, isSM } = useResponsiveness();
  const styles = useMemo(
    () => createStyles(colors, palette, isXS, isSM, layout),
    [colors, palette, isXS, isSM, layout]
  );
  return (
    <View style={styles.card}>
      <View style={styles.headerBand}>
        <View style={[styles.placeholderBar, { width: 90 }]} />
        <View style={[styles.placeholderBar, { width: 50 }]} />
      </View>
      <View style={styles.body}>
        <View style={styles.titleRow}>
          <View style={[styles.glyph, { opacity: 0.5 }]} />
          <View style={{ flex: 1, gap: 6 }}>
            <View style={[styles.placeholderBar, { width: '70%', height: 12 }]} />
            <View style={[styles.placeholderBar, { width: '40%', height: 10 }]} />
          </View>
        </View>
      </View>
    </View>
  );
}

const createStyles = (colors, palette, isXS, isSM, layout) => {
  const list = layout === 'list';
  const wideList = list && !isSM;
  // Compact tightens padding and trims the glyph on tablet (isMD) and
  // below so the card body still fits two lines plus the meta row in a
  // 1-up column on mobile.
  const compact = isSM;
  const bodyPad = isXS ? 16 : 18;
  const cardMinH = isXS ? 130 : compact ? 140 : 150;

  return StyleSheet.create({
  card: {
    minHeight: list ? undefined : cardMinH,
    flexDirection: wideList ? 'row' : 'column',
    borderRadius: list ? 14 : 18,
    borderWidth: 1,
    borderColor: palette.border,
    backgroundColor: palette.surface,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  cardHovered: {
    borderColor: palette.activeBorder,
    backgroundColor: palette.hover,
  },
  headerBand: {
    flexDirection: wideList ? 'column' : 'row',
    alignItems: wideList ? 'flex-start' : 'center',
    width: wideList ? 174 : undefined,
    gap: 10,
    justifyContent: 'space-between',
    paddingHorizontal: bodyPad,
    paddingVertical: wideList ? 16 : 10,
    backgroundColor: list ? 'transparent' : palette.preview,
    borderBottomWidth: list ? 0 : StyleSheet.hairlineWidth,
    borderBottomColor: palette.border,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    flex: wideList ? undefined : 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  body: {
    padding: bodyPad,
    paddingTop: list && !wideList ? 4 : bodyPad,
    flex: wideList ? 1 : undefined,
    minWidth: 0,
    flexDirection: wideList ? 'row' : 'column',
    alignItems: wideList ? 'center' : 'stretch',
    gap: wideList ? 20 : 12,
  },
  content: { flex: wideList ? 1 : undefined, minWidth: 0, gap: 10 },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
    paddingTop: wideList ? 0 : 12,
    borderTopWidth: wideList ? 0 : StyleSheet.hairlineWidth,
    borderTopColor: palette.border,
  },
  runBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 12, paddingVertical: 9,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: palette.border,
  },
  linkBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 10, paddingVertical: 9,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
  },
  placeholderBar: {
    height: 10,
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 4,
  },
  });
};

export default React.memo(TaskCardCalendar);
